import React, { useEffect, useRef, useState } from 'react';

import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import 'react-datepicker/dist/react-datepicker.css';
import * as yup from 'yup';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import Element from '../../components/element';
import Button from '../../components/Button';
import Flex from '../../components/flex';
import Text from '../../components/text';
import useStyles from './style';
import TextField from '../../components/TextField';
import Select from '../../components/Select';
import Modal from '../../components/modal';
import IconClock from '../../assets/icons/IconClock.svg';
import IconLocation from '../../assets/icons/IconLocation.svg';
import IconLocationLarge from '../../assets/icons/IconLocationLarge.svg';
import useToast from '../../components/Toast/useToast';
import IconWarning from '../../assets/icons/IconWarning.svg';
import { getTimeWithAmOrPm } from '../../utils';
import {
  GET_LOCATIONS,
  GET_BLOCKED_DATES_BY_LOCATION,
  GET_AVAILABLE_TIMES_BY_DATE,
  VISIT_URL
} from '../../api/endpoints';
import { apiRequest } from '../../api/actions';
import { ReactComponent as IconCalender } from '../../assets/icons/IconCalender.svg';
import { colors } from '../../config/theme';

import { appointmentPaymentSchema } from '../../config/schema';

const schema = yup.object(appointmentPaymentSchema).required();
const getUser = state => {
  return state.containers.app.get('user');
};
const paymentType = ['Credit Card', 'HSA/FSA', 'PayPal'];

const ScheduleAppointment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { appointmentDetail } = state || {};
  const user = useSelector(getUser);
  const classes = useStyles();
  const [openSnackbar] = useToast();
  const calenderRef = useRef(null);

  const [stepper, setStepper] = useState({
    currentIndex: 0,
    components: ['locationDateTimeSelection', 'payment']
  });
  const [formData, setFormData] = useState({
    location: '',
    date: '',
    time: ''
  });
  const [showTime, setShowTime] = useState(false);
  const [locations, setLocations] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [times, setTimes] = useState([]);
  const [disableNextBtn, setDisableNextBtn] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [appointmentCost, setAppointmentCost] = useState('');
  const [cancellationCost, setCancellationCost] = useState('');
  const [selectedPaymentType, setSelectedPaymentType] = useState('');
  const [states, setStates] = useState([]);
  const [prevAppointmentDetail, setPrevAppointmentDetail] = useState({});
  const [paymentTypeErr, setPaymentTypeErr] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [tempCardNumber, setTempCardNumber] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  const getBlockedDatesByLocation = locationId => {
    const request = {
      method: 'post',
      url: GET_BLOCKED_DATES_BY_LOCATION,
      data: { _id: locationId }
    };
    const meta = {
      onSuccess: data => {
        if (data.data.error?.code) {
          openSnackbar(data.data.message, 'warn');
          return;
        }
        const blockedDates = data?.data?.data;
        setBlockedDates(blockedDates);
      }
    };

    dispatch(apiRequest(request, meta));
  };

  const getAvailableTimesByDate = _ => {
    const request = {
      method: 'post',
      url: GET_AVAILABLE_TIMES_BY_DATE,
      data: {
        _id: formData.location,
        date: moment(formData.date).format('YYYY-MM-DD')
      }
    };
    const meta = {
      onSuccess: data => {
        if (data.data.error?.code) {
          openSnackbar(data.data.message, 'warn');
          return;
        }
        const blockedDates = data?.data?.data;
        setTimes(blockedDates);
      }
    };

    dispatch(apiRequest(request, meta));
  };

  const onChange = e => {
    const { name, value } = e.target;

    if (name === 'date' || name === 'location') {
      setTimes([]);
      setDisableNextBtn(true);
      if (name === 'location') {
        getBlockedDatesByLocation(value);
        const appFee = locations.filter(location => location._id === value)[0]
          ?.appointmentFee;
        const cancelFee = locations.filter(
          location => location._id === value
        )[0]?.cancellationFee;
        setAppointmentCost(appFee >= 0 ? appFee : '');
        setCancellationCost(cancelFee >= 0 ? cancelFee : '');
        setFormData({ location: value, date: '', time: '' });
        setShowTime(false);
      }
      if (name === 'date')
        setFormData({ location: formData.location, date: value, time: '' });
    }
    if (name === 'time') {
      setFormData({ ...formData, [name]: value });
      setDisableNextBtn(false);
    }
  };
  const openDatePicker = () => {
    calenderRef.current.setOpen(true);
  };

  const displayError = (key, isMsg) => (
    <Flex marginTop="5px">
      <Element type="img" attrs={{ src: IconWarning }} marginRight="5px" />
      <Text family="GTAM" color={colors.ceriseRed}>
        {isMsg ? key : errors[key]?.message}
      </Text>
    </Flex>
  );

  useEffect(() => {
    const request = {
      method: 'get',
      url: GET_LOCATIONS
    };
    const meta = {
      onSuccess: data => {
        if (data.data.error?.code) {
          openSnackbar(data.data.message, 'warn');
          return;
        }
        const locations = data?.data?.data;
        setLocations(locations);
      }
    };

    dispatch(apiRequest(request, meta));

    const stateRequest = {
      method: 'get',
      url: `/lookup/countries/233/states`
    };
    const stateMeta = {
      onSuccess: data => {
        if (data.data.error?.code) {
          openSnackbar(data.data.message, 'warn');
          return;
        }
        const allStates = data?.data?.data;
        setStates(allStates);
      }
    };

    dispatch(apiRequest(stateRequest, stateMeta));
  }, []);

  useEffect(() => {
    const { location, date, time } = formData;
    if (location && date) {
      setShowTime(true);
    }
  }, [formData]);

  useEffect(() => {
    if (appointmentDetail) {
      setPrevAppointmentDetail(appointmentDetail);
      getBlockedDatesByLocation(appointmentDetail?.clinic?._id);
      setFormData({
        ...formData,
        location: appointmentDetail?.clinic?._id
      });
    }
  }, [appointmentDetail]);

  useEffect(() => {
    const { location, date } = formData;
    if (location && date) {
      getAvailableTimesByDate();
    }
  }, [formData.location, formData.date]);

  const locationDateTimeSelection = () => (
    <Flex justify="space-between">
      <Element>
        <Element>
          <Element
            type="h5"
            fontSize="16px"
            fontFamily="GTAM"
            color={colors.mineShaft}
            marginBottom="16px"
          >
            Select a location
          </Element>
          <Element width="200px">
            <select
              name="location"
              onChange={onChange}
              disabled={'clinic' in prevAppointmentDetail}
            >
              <option value="">Please select location</option>
              {locations.map(location => (
                <option
                  value={location._id}
                  selected={
                    location.name === prevAppointmentDetail?.clinic?.name ||
                    location._id === formData.location
                  }
                >
                  {location.name}
                </option>
              ))}
            </select>
          </Element>
        </Element>
        <Element marginTop="30px">
          <Element
            type="h5"
            fontSize="16px"
            fontFamily="GTAM"
            color={colors.mineShaft}
            marginBottom="16px"
          >
            Select a date to see available appointment times.
          </Element>
          <Element width="200px" position="relative">
            {/* <input type="date" name="date" onChange={onChange} /> */}
            <DatePicker
              selected={formData.date}
              onChange={date => {
                let e = {
                  target: {
                    name: 'date',
                    value: date
                  }
                };
                onChange(e);
              }}
              minDate={new Date()}
              excludeDates={blockedDates.map(date => new Date(date))}
              placeholderText="Select a date..."
              disabled={
                appointmentCost === '' && !('clinic' in prevAppointmentDetail)
              }
              ref={calenderRef}
            />
            <Element
              onClick={openDatePicker}
              type={IconCalender}
              position="absolute"
              right={0}
              bottom={0}
              width="24px"
              height="24px"
            />
          </Element>
        </Element>
        {showTime && (
          <Element marginTop="32px">
            <Element
              type="h5"
              fontSize="16px"
              fontFamily="GTAM"
              color={colors.mineShaft}
              marginBottom="22px"
            >
              Select a Time
            </Element>
            <Element>
              {times.length ? (
                times.map(time => (
                  <button
                    name="time"
                    value={time}
                    style={{
                      backgroundColor:
                        formData.time === time ? colors.chetwodeBlue : 'white',
                      color: colors.mineShaft,
                      width: '130px',
                      border: `1px solid ${
                        formData.time === time
                          ? colors.turquoiseBlue
                          : colors.dustyGray
                      }`,
                      margin: '5px'
                    }}
                    onClick={onChange}
                  >
                    {getTimeWithAmOrPm(time)}
                  </button>
                ))
              ) : (
                <Element
                  fontSize="21px"
                  textAlign="center"
                  color={colors.dustyGray}
                >
                  There are currently no available time slots for the date you
                  selected.
                </Element>
              )}
            </Element>
          </Element>
        )}
      </Element>
      {appointmentDetail && (
        <Element
          padding="16px 24px"
          border={`1px solid ${colors.lightGray}`}
          borderRadius="4px"
        >
          <Text type="h5" family="GTAM" size="16px" marginBottom="18px">
            You are currently scheduled for:
          </Text>
          <Element marginBottom="24px">
            <Flex align="start">
              <Element
                type="img"
                attrs={{ src: IconLocationLarge }}
                marginRight="15px"
                width="24px"
                height="24px"
              />
              <Element>
                <Text type="h5" size="16px" margin="0">
                  {prevAppointmentDetail?.clinic?.name}
                </Text>
                <Text type="h5" size="16px" margin="0">
                  {`${prevAppointmentDetail?.clinic?.address1}`}
                </Text>
                <Text type="h5" size="16px" margin="0">
                  {`${prevAppointmentDetail?.clinic?.city}, ${prevAppointmentDetail?.clinic?.state.name}, ${prevAppointmentDetail?.clinic?.zipcode}`}
                </Text>
              </Element>
            </Flex>
          </Element>
          <Element>
            <Flex align="center">
              <>
                <Element
                  type={IconCalender}
                  width="24px"
                  height="24px"
                  marginRight="5px"
                />
                <Text marginRight="5px">
                  {`${moment(prevAppointmentDetail?.visitDate).format('LL')}`}
                </Text>
                <Text margin="0 16px">at</Text>
              </>
              <>
                <Element
                  type="img"
                  attrs={{ src: IconClock }}
                  marginRight="5px"
                />
                <Text>
                  {getTimeWithAmOrPm(prevAppointmentDetail?.scheduledTime)}
                </Text>
              </>
            </Flex>
          </Element>
        </Element>
      )}
    </Flex>
  );

  const onSubmit = () => {
    const values = getValues();
    const {
      name,
      cardNumber,
      expDate,
      cvv,
      address,
      address2,
      city,
      state,
      zip
    } = values;
    const { location, date, time } = formData;
    const { email, mrn, id } = user;
    const payload = {
      scheduledDate: moment(date).format('YYYY-MM-DD'),
      scheduledTime: time,
      payment:
        appointmentCost === 0
          ? {}
          : {
              number: cardNumber,
              expiration: expDate,
              code: cvv,
              nameOnCard: name,
              paymentType: selectedPaymentType,
              address1: address,
              address2,
              city,
              state,
              zipCode: zip
            },
      patient: {
        email,
        mrn,
        id
      },
      clinic: {
        id: location,
        visitCost: appointmentCost
      }
    };
    const request = {
      method: 'post',
      url: VISIT_URL,
      data: payload
    };
    const meta = {
      onSuccess: res => {
        if (res.data.error?.code) {
          openSnackbar(res.data.message, 'warn');
          return;
        }
        openSnackbar(
          'Your appointment is scheduled. We look forward to seeing you soon!'
        );
        localStorage.removeItem('isUserUpdatedSurvey');
        navigate('/landing/appointment/view');
      },
      onerror: res => {
        openSnackbar(res.data.message, 'warn');
        onClickReviewCancel();
      }
    };

    dispatch(apiRequest(request, meta));
  };

  const reScheduleSubmit = () => {
    const { location, date, time } = formData;
    const payload = {
      scheduledDate: moment(date).format('YYYY-MM-DD'),
      scheduledTime: time,
      clinic: {
        id: location
      }
    };
    const request = {
      method: 'put',
      url: `/me/visit/${appointmentDetail?._id}`,
      data: payload
    };
    const meta = {
      onSuccess: res => {
        if (res.data.error?.code) {
          openSnackbar(res.data.message, 'warn');
          return;
        }
        openSnackbar(
          'Your appointment is rescheduled. We look forward to seeing you soon!'
        );

        navigate('/landing/appointment/view');
      },
      onerror: res => {
        openSnackbar(res.data.message, 'warn');
      }
    };

    dispatch(apiRequest(request, meta));
  };

  const onChangeCardNumber = (e, field) => {
    const num = e.target.value;
    setCardNumber(num);
    setTempCardNumber(num);
    if (field) {
      field.onChange(num);
    }
  };

  const maskInput = () => {
    setCardNumber(
      tempCardNumber
        .split('')
        .map(x => 'x')
        .join('')
    );
  };

  const unMaskInput = () => {
    setCardNumber(tempCardNumber);
  };

  const fullAddress = (data, state) => {
    if (data) {
      if (data?.address1) {
        return `${data?.name} @ ${data?.address}, ${data?.address1}, ${data?.city}, ${state.name} ${data?.zipcode}`;
      }
      return `${data?.name} @ ${data?.address}, ${data?.city}, ${state.name} ${data?.zipcode}`;
    }
    return '--';
  };

  const validateNumber = e => {
    const value = e.target.value;
    // Allow only numbers, including 0
    if (!/^\d*$/.test(value)) {
      e.target.value = value.slice(0, -1);
    }
  };

  const payment = () => {
    return (
      <form id="paymentForm" onSubmit={handleSubmit(onSubmit)}>
        <Element
          border={`1px solid ${colors.lightGray}`}
          borderRadius="4px"
          display="inline-block"
          padding="16px 24px"
        >
          <Element type="h5" fontSize="16px" fontFamily="GTAM" margin="0">
            Appointment Cost
          </Element>
          <Element
            type="h4"
            fontSize="21px"
            fontFamily="GTAM"
            margin="0"
            marginTop="8px"
          >
            {`$${parseFloat(appointmentCost).toFixed(2)}`}
          </Element>
        </Element>
        <Element marginTop="32px">
          <Element
            type="h5"
            fontSize="16px"
            fontFamily="GTAM"
            margin="0"
            marginBottom="16px"
          >
            Payment Type
          </Element>
          <Flex>
            {paymentType.map(payment => (
              <button
                type="button"
                name="paymentType"
                value={payment}
                style={{
                  fontSize: '16px',
                  padding: '9px 15px',
                  backgroundColor:
                    selectedPaymentType === payment
                      ? colors.chetwodeBlue
                      : 'white',
                  color: colors.mineShaft,
                  border: `1px solid ${
                    selectedPaymentType === payment
                      ? colors.turquoiseBlue
                      : colors.dustyGray
                  }`,
                  marginRight: '5px'
                }}
                onClick={() => {
                  setSelectedPaymentType(payment);
                  setPaymentTypeErr('');
                }}
              >
                {payment}
              </button>
            ))}
          </Flex>
          {paymentTypeErr && displayError(paymentTypeErr, true)}
        </Element>
        <Element marginTop="32px">
          <Element
            type="h5"
            fontSize="16px"
            fontFamily="GTAM"
            margin="0"
            marginBottom="16px"
          >
            Payment Information
          </Element>
          <div className={classes.field}>
            <TextField
              name="name"
              label="Name on Card"
              errors={errors}
              register={register}
            />
          </div>
          <div className={classes.field}>
            <Flex>
              <Element width="60%" marginRight="10px">
                <label>Card Number</label>
                <Controller
                  control={control}
                  name="cardNumber"
                  render={({ field }) => {
                    return (
                      <InputMask
                        {...field}
                        mask="****************"
                        maskChar={null}
                        onChange={e => onChangeCardNumber(e, field)}
                        onFocus={unMaskInput}
                        onBlur={maskInput}
                        value={cardNumber}
                        style={{ width: '100%' }}
                      />
                    );
                  }}
                />
                {errors.cardNumber?.message && displayError('cardNumber')}
              </Element>
              <Element width="30%" marginRight="10px">
                <label>Exp Date</label>
                <Controller
                  control={control}
                  name="expDate"
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      mask="99/9999"
                      placeholder="MM/YYYY"
                      maskChar={null}
                      style={{ width: '100%' }}
                    />
                  )}
                />
                {errors.expDate?.message && displayError('expDate')}
              </Element>
              <Element width="20%">
                <label>CVV</label>
                <Controller
                  control={control}
                  name="cvv"
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      mask="9999"
                      placeholder="123"
                      maskChar={null}
                      style={{ width: '100%' }}
                    />
                  )}
                />
                {errors.cvv?.message && displayError('cvv')}
              </Element>
            </Flex>
          </div>
        </Element>
        <Element marginTop="32px">
          <Element
            type="h5"
            fontSize="16px"
            fontFamily="GTAM"
            margin="0"
            marginBottom="16px"
          >
            Billing Address
          </Element>
          <Flex>
            <div className={classes.field}>
              <TextField
                name="address"
                label="Street Address"
                errors={errors}
                register={register}
              />
            </div>
            <div className={classes.field}>
              <TextField
                name="address2"
                label="&nbsp;"
                errors={errors}
                register={register}
              />
            </div>
          </Flex>
          <Flex>
            <div className={classes.field}>
              <TextField
                name="city"
                label="City"
                errors={errors}
                register={register}
              />
            </div>
            <div style={{ width: '50%' }}>
              <Flex>
                <div className={classes.field}>
                  <Select
                    label="State"
                    name="state"
                    errors={errors}
                    register={register}
                    mainOption="Select State"
                    options={states.map(state => {
                      return { name: state.name, value: state._id };
                    })}
                  />
                </div>
                <Element
                  className={classes.field}
                  width="100%"
                  marginRight="unset"
                >
                  <TextField
                    type="text"
                    onChange={validateNumber}
                    name="zip"
                    label="Zip or Postal Code"
                    errors={errors}
                    register={register}
                  />
                </Element>
              </Flex>
            </div>
          </Flex>
        </Element>
      </form>
    );
  };

  const reviewAppointment = () => {
    const values = getValues();
    const {
      name,
      cardNumber,
      expDate,
      cvv,
      address,
      address2,
      city,
      state,
      zip
    } = values;
    const { location, date, time } = formData;
    const appointmentLocation = locations.filter(
      data => data._id === location
    )[0];
    const stateDetail = states.filter(
      state => state._id === appointmentLocation.stateId
    )[0];
    return (
      <Modal
        headerLabel="Review Appointment Details"
        closable={true}
        onClose={() => setShowReviewModal(false)}
        width="50%"
      >
        <Text family="GTAR">
          Please review and confirm the details below so we can get your
          appointment on the books. Payment will be collected once confirmation
          is made.
        </Text>
        <hr />
        <Element marginTop="42px" marginBottom="25px">
          <Element
            type="h5"
            fontSize="16px"
            fontFamily="GTAM"
            color={colors.mineShaft}
            marginBottom="15px"
          >
            Location
          </Element>
          <Flex align="center">
            <Element
              type="img"
              attrs={{ src: IconLocation }}
              marginRight="5px"
            />
            <Text>{fullAddress(appointmentLocation, stateDetail) || '--'}</Text>
          </Flex>
        </Element>
        <Element marginBottom="50px">
          <Element
            type="h5"
            fontSize="16px"
            fontFamily="GTAM"
            color={colors.mineShaft}
            marginBottom="15px"
          >
            Date & Time
          </Element>
          <Element>
            <Flex align="center">
              <>
                <Element
                  type={IconCalender}
                  width="24px"
                  height="24px"
                  marginRight="8px"
                />
                <Text>{moment(date).format('LL')}</Text>
                <Text margin="16px">at</Text>
              </>
              <>
                <Element
                  type="img"
                  attrs={{ src: IconClock }}
                  marginRight="8px"
                />
                <Text>{getTimeWithAmOrPm(time)}</Text>
              </>
            </Flex>
          </Element>
          <hr />
        </Element>
        {!appointmentDetail && !(appointmentCost === 0) && (
          <>
            <Element>
              <Element
                type="h5"
                fontSize="16px"
                fontFamily="GTAM"
                color={colors.mineShaft}
                marginBottom="15px"
              >
                Payment Information
              </Element>
              <Element>
                <Element
                  // type="h5"
                  fontSize="12px"
                  fontFamily="GTAM"
                  color={colors.mineShaft}
                  marginBottom="11px"
                >
                  Name on Card
                </Element>
                <Text>{name}</Text>
              </Element>
              <Flex marginTop="30px" width="45%" justify="space-between">
                <Element>
                  <Element
                    // type="h5"
                    fontSize="12px"
                    fontFamily="GTAM"
                    color={colors.mineShaft}
                    marginBottom="11px"
                  >
                    Card Number
                  </Element>
                  <Text>xxxx xxxx xxxx xxxx</Text>
                </Element>
                <Element>
                  <Element
                    // type="h5"
                    fontSize="12px"
                    fontFamily="GTAM"
                    color={colors.mineShaft}
                    marginBottom="11px"
                  >
                    Exp Date
                  </Element>
                  <Text>{expDate}</Text>
                </Element>
                <Element>
                  <Element
                    // type="h5"
                    fontSize="12px"
                    fontFamily="GTAM"
                    color={colors.mineShaft}
                    marginBottom="11px"
                  >
                    CVV
                  </Element>
                  <Text>{cvv}</Text>
                </Element>
              </Flex>
            </Element>
            <Element marginTop="30px">
              <Element
                type="h5"
                fontSize="16px"
                fontFamily="GTAM"
                color={colors.mineShaft}
                marginBottom="15px"
              >
                Billing Address
              </Element>
              <Element>
                <Element
                  // type="h5"
                  fontSize="12px"
                  fontFamily="GTAM"
                  color={colors.mineShaft}
                  marginBottom="11px"
                >
                  Street Address
                </Element>
                <Text>{`${address}, ${address2}`}</Text>
              </Element>
              <Flex marginTop="30px" width="60%" justify="space-between">
                <Element>
                  <Element
                    // type="h5"
                    fontSize="12px"
                    fontFamily="GTAM"
                    color={colors.mineShaft}
                    marginBottom="11px"
                  >
                    City
                  </Element>
                  <Text>{city}</Text>
                </Element>
                <Element>
                  <Element
                    // type="h5"
                    fontSize="12px"
                    fontFamily="GTAM"
                    color={colors.mineShaft}
                    marginBottom="11px"
                  >
                    State
                  </Element>
                  <Text>
                    {states.filter(
                      data => data?._id?.toString() === state?.toString()
                    )[0]?.name || '--'}
                  </Text>
                </Element>
                <Element>
                  <Element
                    // type="h5"
                    fontSize="12px"
                    fontFamily="GTAM"
                    color={colors.mineShaft}
                    marginBottom="11px"
                  >
                    Zip or Postal Code
                  </Element>
                  <Text>{zip}</Text>
                </Element>
              </Flex>
              <hr />
            </Element>
            <Element type="h4" width="70%" marginTop="40px">
              <Flex justify="space-between">
                <Text size="21px" family="GTAR">
                  Appointment Cost:
                </Text>
                <Text size="21px" family="GTAM">
                  {`$${parseFloat(appointmentCost).toFixed(2)}`}
                </Text>
              </Flex>
            </Element>
            <Text
              family="GTAR"
              color={colors.dustyGray}
              width="100%"
              marginTop="17px"
            >
              Note that a ${cancellationCost} fee will automatically be charged
              to your credit card for canceling an appointment less than 24
              hours prior to the scheduled date & time.
            </Text>
          </>
        )}
        <Flex marginTop="40px">
          <Button
            scheme="light"
            style={{
              marginRight: '15px',
              border: `1px solid ${colors.dustyGray}`,
              color: colors.dustyGray
            }}
            onClick={onClickReviewCancel}
          >
            Cancel
          </Button>
          <Button
            form={
              appointmentDetail || appointmentCost === 0 ? '' : 'paymentForm'
            }
            type={
              appointmentDetail || appointmentCost === 0 ? 'button' : 'submit'
            }
            onClick={() => {
              if (appointmentDetail) {
                reScheduleSubmit();
              }
              if (appointmentCost === 0) {
                onSubmit();
              }
            }}
          >
            {appointmentDetail ? 'Confirm & Reschedule' : 'Confirm'}
          </Button>
        </Flex>
      </Modal>
    );
  };

  const chooseStep = () => {
    const component = stepper.components[stepper.currentIndex];
    switch (component) {
      case 'locationDateTimeSelection':
        return locationDateTimeSelection();
      case 'payment':
        return payment();
    }
  };

  const onClickPrev = () => {
    setStepper({ ...stepper, currentIndex: stepper.currentIndex - 1 });
  };

  const onClickNext = async () => {
    let value = stepper.currentIndex;
    if (appointmentCost === 0) {
      value = stepper.currentIndex + 2;
    } else {
      value = stepper.currentIndex + 1;
    }

    if (value === stepper.components.length || appointmentDetail) {
      const isSuccess = await trigger(undefined, { shouldFocus: true });
      if (
        (isSuccess && selectedPaymentType) ||
        appointmentDetail ||
        appointmentCost === 0
      )
        setShowReviewModal(true);
      if (!selectedPaymentType) {
        setPaymentTypeErr('Please select payment type');
      }
      return;
    }
    setStepper({ ...stepper, currentIndex: stepper.currentIndex + 1 });
  };

  const onClickCancel = () => {
    navigate('/landing/assessments');
  };

  const onClickReviewCancel = () => {
    setShowReviewModal(false);
  };

  const getDesc = () => {
    return appointmentDetail
      ? 'Select a date to see available appointment times.'
      : !formData.location
      ? 'Select a location and a date to see available appointment times.'
      : !formData.date || !formData.time || !stepper.currentIndex
      ? 'Select a date to see available appointment times.'
      : 'Please select a payment type and fill in your payment information. All fields are required.';
  };

  return (
    <Element padding="15px 25px">
      <Element
        type="h4"
        fontSize="21px"
        fontFamily="GTAM"
        color={colors.mineShaft}
        margin="8px 0"
      >
        {appointmentDetail ? 'Reschedule Your Visit' : 'Schedule Your Visit'}
      </Element>
      <Element type="p" fontFamily="GTAR" fontSize="14px" marginBottom="35px">
        {getDesc()}
      </Element>
      {/* {payment()} */}
      {chooseStep()}
      {showReviewModal && reviewAppointment()}
      <Flex marginTop="90px">
        <Button
          scheme="light"
          style={{
            border: `1px solid ${colors.dustyGray}`,
            color: colors.dustyGray
          }}
          onClick={onClickCancel}
        >
          Cancel
        </Button>
        <Element marginLeft="30%">
          {stepper.currentIndex > 0 && (
            <Button
              onClick={onClickPrev}
              disabled={disableNextBtn}
              style={{ marginRight: '15px' }}
            >
              Previous
            </Button>
          )}
          <Button onClick={onClickNext} disabled={disableNextBtn}>
            {appointmentDetail ? 'Submit' : 'Next'}
          </Button>
        </Element>
      </Flex>
    </Element>
  );
};

export default ScheduleAppointment;
